import { createTheme, alpha } from '@mui/material/styles';

/**
 * The panel's look, taken from the logo.
 *
 * The mark is two colours: a red (#db1f1f) and a near-black grey (#4f4c4c).
 * The red is **not** the primary action colour, which is the obvious move and
 * the wrong one — a panel whose every button is red reads as a panel of
 * warnings, and it leaves nothing to say "this will delete something" with.
 * So the structure is graphite and the red is the accent: the active page in
 * the sidebar, a link, the one button on the login screen. It appears rarely
 * enough to still mean something, which is how it works on the lorry too.
 *
 * Surfaces are white on a warm grey, separated by borders. They now also
 * carry a **very** shallow shadow — two pixels of spread at four per cent —
 * which was added when the owner asked for a panel with more depth to it. The
 * original note here said a drop shadow under every card is noise around the
 * thing being read, and that is still true of a real shadow; this one is below
 * the threshold of looking like one and only stops the cards from lying flat
 * on the canvas. The border is still what separates them.
 *
 * Colour does the rest of the work, and does it through `IconTile`: a tinted
 * rounded square behind an icon, in one of five tones. That is where the panel
 * gets its colour, rather than from filled surfaces — a tint at a tenth
 * strength reads as a category, while a saturated card reads as an alarm.
 *
 * There are two of these, built from one description. Dark is not the light
 * palette inverted: its greys keep the logo's warmth rather than going blue,
 * nothing is pure black (a depot office is never dark enough for that, and
 * pure black makes borders invisible), and the red is lifted to #f04a44,
 * because the brand red on a near-black surface is too dim to read.
 */

const BRAND_RED = '#db1f1f';
const GRAPHITE = '#332f2f';

/** Everything that differs between the two. */
const SURFACES = {
  light: {
    accent: BRAND_RED,
    accentHover: '#a81717',
    structure: GRAPHITE,
    structureHover: '#1f1c1c',
    ink: '#1b1918',
    muted: '#6f6a6a',
    line: '#e6e4e4',
    canvas: '#f6f6f7',
    paper: '#ffffff',
    bar: '#ffffff',
    rail: '#fafafa',
    scrollbar: '#d4d1d1',
    scrollbarHover: '#bdb9b9',
    // Shallow on purpose: see the note at the top of this file.
    cardShadow: '0 1px 2px rgba(20, 18, 18, 0.04), 0 2px 8px rgba(20, 18, 18, 0.04)',
    cardShadowHover: '0 2px 4px rgba(20, 18, 18, 0.06), 0 6px 18px rgba(20, 18, 18, 0.07)',
  },
  dark: {
    // Lifted off the brand red: #db1f1f on a near-black surface reads as dim
    // rather than as the mark.
    accent: '#f04a44',
    accentHover: '#ff6b63',
    // In the dark the structure cannot be graphite — it would vanish into the
    // page — so the solid button becomes the light surface instead.
    structure: '#e9e5e4',
    structureHover: '#ffffff',
    ink: '#f2efee',
    muted: '#a39d9c',
    line: '#332f2f',
    canvas: '#141212',
    paper: '#1b1919',
    bar: '#1b1919',
    rail: '#211e1e',
    scrollbar: '#3b3636',
    scrollbarHover: '#4d4747',
    // Heavier than the light theme's, because a shadow on a near-black canvas
    // has almost nothing to darken; without this the dark cards stay flat.
    cardShadow: '0 1px 2px rgba(0, 0, 0, 0.4), 0 2px 10px rgba(0, 0, 0, 0.3)',
    cardShadowHover: '0 2px 6px rgba(0, 0, 0, 0.5), 0 8px 22px rgba(0, 0, 0, 0.4)',
  },
} as const;

export const THEME_MODES = ['light', 'dark'] as const;
export type ThemeMode = (typeof THEME_MODES)[number];

/**
 * No webfont. The theme used to name Inter, which was never loaded, so the
 * panel has always rendered in the system stack — now it says so. A van depot
 * on a slow line should not wait on a font to read a stock figure.
 */
const SYSTEM_FONTS = [
  'system-ui',
  '-apple-system',
  'Segoe UI',
  'Roboto',
  'Helvetica Neue',
  'Arial',
  'sans-serif',
].join(', ');

export function buildTheme(mode: ThemeMode) {
  const c = SURFACES[mode];

  return createTheme({
    palette: {
      mode,
      primary: {
        main: c.structure,
        dark: c.structureHover,
        contrastText: mode === 'light' ? '#ffffff' : '#1b1918',
      },
      secondary: { main: c.accent, dark: c.accentHover, contrastText: '#ffffff' },
      // Kept clearly apart from the brand red: this one only ever means trouble.
      error: { main: mode === 'light' ? '#9b2226' : '#e5706b' },
      warning: { main: mode === 'light' ? '#a8630a' : '#d99a3c' },
      success: { main: mode === 'light' ? '#1c7a4b' : '#4caf7d' },
      info: { main: mode === 'light' ? '#15616d' : '#4bb3c4' },
      background: { default: c.canvas, paper: c.paper },
      text: { primary: c.ink, secondary: c.muted },
      divider: c.line,
    },

    shape: { borderRadius: 12 },

    typography: {
      fontFamily: SYSTEM_FONTS,
      h1: { fontSize: '1.625rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 },
      h2: { fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.01em' },
      h3: { fontSize: '1.0625rem', fontWeight: 600 },
      h6: { fontSize: '1rem', fontWeight: 700, letterSpacing: '-0.01em' },
      subtitle2: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
      // Table figures and codes sit next to each other down a column; same-width
      // digits make them comparable at a glance.
      body2: { fontVariantNumeric: 'tabular-nums' },
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: c.canvas,
            WebkitFontSmoothing: 'antialiased',
          },
          // The default scrollbar is a wide grey slab against white surfaces.
          '*::-webkit-scrollbar': { width: 10, height: 10 },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: c.scrollbar,
            borderRadius: 8,
            border: '2px solid transparent',
            backgroundClip: 'content-box',
          },
          '*::-webkit-scrollbar-thumb:hover': { backgroundColor: c.scrollbarHover },
        },
      },

      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'inherit' },
        styleOverrides: {
          root: {
            backgroundColor: c.bar,
            backgroundImage: 'none',
            color: c.ink,
            borderBottom: `1px solid ${c.line}`,
            // Three pixels of the mark across the top of the window. The red
            // is the accent and appears rarely (see above); this is the one
            // place it is allowed to be constant, because it is the frame
            // rather than a thing inside the page competing for attention.
            borderTop: `3px solid ${c.accent}`,
          },
        },
      },

      MuiDrawer: {
        styleOverrides: {
          paper: { backgroundColor: c.paper, borderRight: `1px solid ${c.line}` },
        },
      },

      MuiListItemButton: {
        styleOverrides: {
          root: {
            position: 'relative',
            borderRadius: 10,
            marginInline: 8,
            paddingBlock: 8,
            color: c.muted,
            '& .MuiListItemIcon-root': { minWidth: 34, color: 'inherit' },
            '& .MuiListItemText-primary': { fontSize: '0.9375rem', fontWeight: 500 },
            '&:hover': { backgroundColor: alpha(c.ink, 0.06), color: c.ink },
            '&.Mui-selected': {
              backgroundColor: alpha(c.accent, mode === 'light' ? 0.09 : 0.16),
              color: c.accent,
              '& .MuiListItemText-primary': { fontWeight: 650 },
              '&:hover': { backgroundColor: alpha(c.accent, mode === 'light' ? 0.13 : 0.22) },
              // A bar down the leading edge. The tint alone says "selected"
              // only once you have compared it with the others; the bar says
              // it without comparison, which is what a sidebar is scanned for.
              '&::before': {
                content: '""',
                position: 'absolute',
                insetInlineStart: 0,
                top: 7,
                bottom: 7,
                width: 3,
                borderRadius: 3,
                backgroundColor: c.accent,
              },
            },
            '&.Mui-disabled': { opacity: 0.45 },
          },
        },
      },

      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 8, paddingInline: 16 },
          containedPrimary: { '&:hover': { backgroundColor: c.structureHover } },
          outlined: { borderColor: c.line },
        },
      },

      MuiPaper: {
        styleOverrides: {
          outlined: { borderColor: c.line, borderRadius: 14 },
          rounded: { borderRadius: 14 },
        },
      },

      MuiCard: {
        defaultProps: { variant: 'outlined' },
        styleOverrides: {
          root: {
            borderRadius: 14,
            boxShadow: c.cardShadow,
            // Only the transition is declared here, not a hover state: most
            // cards in this panel are not pressable, and a card that lifts
            // under the cursor while nothing happens on click is a lie. The
            // ones that do open something set `cardShadowHover` themselves.
            transition: 'box-shadow 160ms ease, border-color 160ms ease',
          },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: c.line },
          head: {
            backgroundColor: c.rail,
            color: c.muted,
            fontWeight: 700,
            fontSize: '0.75rem',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
          },
          // Nothing here on purpose. A bigger body font made product names wrap
          // and pushed rows to a hundred pixels, and padding of my own only
          // added to it: the table's density is MUI's, and this theme changes
          // how it is coloured, not how much of it fits on a screen.
        },
      },

      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover > .MuiTableCell-body': {
              backgroundColor: alpha(c.ink, mode === 'light' ? 0.025 : 0.05),
            },
            '&:last-of-type > .MuiTableCell-body': { borderBottom: 0 },
          },
        },
      },

      MuiChip: {
        styleOverrides: {
          // Fully round. Every chip in this panel is a status, a role, a plate
          // or a count — a label on something, never a button — and a pill
          // reads as a label where a rounded rectangle reads as a small card.
          root: { borderRadius: 999, fontWeight: 600 },
          sizeSmall: { height: 24 },
          icon: { marginLeft: 6 },
        },
      },

      MuiTextField: { defaultProps: { size: 'small' } },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            backgroundColor: c.paper,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: c.line },
          },
        },
      },

      MuiDialog: { styleOverrides: { paper: { borderRadius: 14 } } },

      MuiAlert: { styleOverrides: { root: { borderRadius: 10 } } },

      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: c.rail === '#fafafa' ? GRAPHITE : '#3a3535',
            fontSize: '0.75rem',
            borderRadius: 7,
          },
          arrow: { color: c.rail === '#fafafa' ? GRAPHITE : '#3a3535' },
        },
      },

      MuiLink: { defaultProps: { color: 'secondary' } },

      MuiDivider: { styleOverrides: { root: { borderColor: c.line } } },
    },
  });
}
